import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import { logError } from "../utils";
import { saveImage, getAllImages, removeImageByKey } from "./indexedDB";
import ApiService from "./apiService";

const ID_TOKEN_KEY_REALTIME_SURVEY_DATA = "real_time_survey_data";
const ID_TOKEN_KEY_ALLSURVEY_DATA = "all_survey_data";
const ID_TOKEN_KEY_REALTIME_SALES_DATA = "real_time_sales_data";
const ID_TOKEN_KEY_ALLSALES_DATA = "all_sales_data";

// [Added by E2logy]: single flight guards. savePendingData runs on a 10 second
// interval, so without these a slow upload was started again by the next tick
// and the same record reached the server more than once.
let isSyncingSurveys = false;
let isSyncingSales = false;

// [Added by E2logy]: removes one entry from a pending list and writes the list
// back. The list is re-read first so two overlapping runs cannot lose entries.
const dropPendingItem = (tokenKey, item) => {
  const current = OfflineService.getItem(tokenKey) || [];
  const serialized = JSON.stringify(item);
  const index = current.findIndex(el => JSON.stringify(el) === serialized);
  if (index >= 0) current.splice(index, 1);
  OfflineService.saveItem(tokenKey, JSON.stringify(current));
};

const OfflineService = {
  initialize() {},

  getItem(tokenKey) {
    return JSON.parse(window.localStorage.getItem(tokenKey));
  },

  saveItem(tokenKey, data) {
    window.localStorage.setItem(tokenKey, data);
  },

  saveImage(files, formBody) {
    // Assuming files is a FileList from the input element
    Array.from(files).forEach(file => {
      const reader = new FileReader();

      reader.onloadend = e => {
        // const base64Image = reader.result;
        const base64Image = e.target.result.split(",")[1];

        // Save the image to IndexedDB
        saveImage(base64Image, formBody)
          .then(() => {
            console.log("Image saved successfully.");
          })
          .catch(error => {
            console.error("Error saving image:", error);
          });
      };

      reader.readAsDataURL(file);
    });
  },

  async checkPendingSurveyData() {
    const pending_data = OfflineService.getItem(
      ID_TOKEN_KEY_REALTIME_SURVEY_DATA
    );
    const images = await getAllImages();
    const pending_all_data = OfflineService.getItem(
      ID_TOKEN_KEY_ALLSURVEY_DATA
    );
    if (
      (pending_data && pending_data.length > 0) ||
      (images && images?.elements && images?.elements.length > 0) ||
      (pending_all_data && pending_all_data.length > 0)
    ) {
      return true;
    }
    return false;
  },

  async checkPendingSalesData() {
    const pending_data = OfflineService.getItem(
      ID_TOKEN_KEY_REALTIME_SALES_DATA
    );
    const pending_all_data = OfflineService.getItem(ID_TOKEN_KEY_ALLSALES_DATA);
    if (
      (pending_data && pending_data.length > 0) ||
      (pending_all_data && pending_all_data.length > 0)
    ) {
      return true;
    }
    return false;
  },

  async saveSales() {
    // [Modified by E2logy]: uploads one record at a time and removes it from
    // storage only after the server confirmed it.
    if (isSyncingSales) return;
    isSyncingSales = true;
    try {
      const pending_data =
        OfflineService.getItem(ID_TOKEN_KEY_REALTIME_SALES_DATA) || [];
      for (const data of pending_data) {
        try {
          await ApiService.postNotAlert(
            `worksession/sales/realtimesavesales`,
            { formData: data }
          );
          dropPendingItem(ID_TOKEN_KEY_REALTIME_SALES_DATA, data);
        } catch (error) {
          console.log("error - ", error);
          break;
        }
      }

      const pending_all_data =
        OfflineService.getItem(ID_TOKEN_KEY_ALLSALES_DATA) || [];
      for (const data of pending_all_data) {
        try {
          await ApiService.postNotAlert(`worksession/sales/savesales`, {
            saveSalesData: data
          });
          dropPendingItem(ID_TOKEN_KEY_ALLSALES_DATA, data);
        } catch (error) {
          console.log("error - ", error);
          break;
        }
      }
    } finally {
      isSyncingSales = false;
    }
  },

  async saveAndUploadSurveys() {
    // [Modified by E2logy]: single flight, sequential, and each record is removed
    // from storage only after the server confirmed it. Photos captured by the
    // current build go through photoQueueService, the legacy image store below is
    // still drained so photos taken with an older build are not lost.
    if (isSyncingSurveys) return;
    isSyncingSurveys = true;
    try {
      let images = await getAllImages();
      if (images) {
        const { keys, elements } = images;
        for (let i = 0; i < elements.length; i++) {
          const image = elements[i];
          let uploadedPhotos = [];
          let { data, formBody } = image;
          try {
            if (
              formBody?.offlineDeleteAttachmentIds &&
              formBody.offlineDeleteAttachmentIds.length > 0
            ) {
              for (const delId of formBody.offlineDeleteAttachmentIds) {
                let deleteBody = {
                  worksessionPosBrandId: formBody.worksessionPosBrandId,
                  surveyId: formBody.surveyId,
                  componentId: formBody.componentId,
                  surveyProOrMatId: formBody.surveyProOrMatId,
                  surveyQuestionId: formBody.surveyQuestionId,
                  worksessionSurveyData: formBody.worksessionSurveyData,
                  worksessionSurveyResponseAttachmentId: delId
                };
                await ApiService.postNotAlert(
                  `worksession/deleteAttachmentItem`,
                  deleteBody
                );
              }
              delete formBody.offlineDeleteAttachmentIds;
            }
            let response = await ApiService.uploadBase64Image(data, true);
            if (response?.data) {
              let uploadedStatusPushItem = {
                photoId: response?.data.id
              };
              uploadedPhotos.push(uploadedStatusPushItem);
            }
            if (uploadedPhotos.length > 0) {
              formBody["uploadedPhotos"] = uploadedPhotos;
            }
            // Remove the image first, then queue the answer, so a failure after
            // this point can never upload the same image twice.
            await removeImageByKey(keys[i]);
            OfflineService.saveOfflineRealTimeData(formBody);
          } catch (error) {
            console.log("legacy offline image error - ", error);
          }
        }
      }

      const pending_data =
        OfflineService.getItem(ID_TOKEN_KEY_REALTIME_SURVEY_DATA) || [];
      for (const data of pending_data) {
        try {
          await ApiService.postNotAlert(
            `worksession/realTimeSaveSurveyResponse`,
            data
          );
          dropPendingItem(ID_TOKEN_KEY_REALTIME_SURVEY_DATA, data);
        } catch (error) {
          console.log("error - ", error);
          break;
        }
      }

      const pending_all_data =
        OfflineService.getItem(ID_TOKEN_KEY_ALLSURVEY_DATA) || [];
      for (const data of pending_all_data) {
        try {
          await ApiService.postNotAlert(
            `worksession/saveSessionSurveyDataV2`,
            data
          );
          dropPendingItem(ID_TOKEN_KEY_ALLSURVEY_DATA, data);
        } catch (error) {
          console.log("error - ", error);
          break;
        }
      }
    } finally {
      isSyncingSurveys = false;
    }
  },

  saveOfflineRealTimeSalesData(data) {
    let savedData = OfflineService.getItem(ID_TOKEN_KEY_REALTIME_SALES_DATA);
    if (!savedData) savedData = [];
    savedData.push(data);
    OfflineService.saveItem(
      ID_TOKEN_KEY_REALTIME_SALES_DATA,
      JSON.stringify(savedData)
    );
  },

  saveOfflineSalesAllData(data) {
    let savedData = OfflineService.getItem(ID_TOKEN_KEY_ALLSALES_DATA);
    if (!savedData) savedData = [];
    savedData.push(data);
    OfflineService.saveItem(
      ID_TOKEN_KEY_ALLSALES_DATA,
      JSON.stringify(savedData)
    );
  },

  replaceOfflineAllSurveyData(data) {
    OfflineService.saveItem(ID_TOKEN_KEY_ALLSURVEY_DATA, JSON.stringify(data));
  },

  replaceOfflineRealTimeData(data) {
    OfflineService.saveItem(
      ID_TOKEN_KEY_REALTIME_SURVEY_DATA,
      JSON.stringify(data)
    );
  },

  replaceOfflineSalesRealTimeData(data) {
    OfflineService.saveItem(
      ID_TOKEN_KEY_REALTIME_SALES_DATA,
      JSON.stringify(data)
    );
  },

  replaceOfflineAllSalesData(data) {
    OfflineService.saveItem(ID_TOKEN_KEY_ALLSALES_DATA, JSON.stringify(data));
  },

  saveOfflineAllData(data) {
    let savedData = OfflineService.getItem(ID_TOKEN_KEY_ALLSURVEY_DATA);
    if (!savedData) savedData = [];
    savedData.push(data);
    OfflineService.saveItem(
      ID_TOKEN_KEY_ALLSURVEY_DATA,
      JSON.stringify(savedData)
    );
  },

  saveOfflineRealTimeData(data) {
    let savedData = OfflineService.getItem(ID_TOKEN_KEY_REALTIME_SURVEY_DATA);
    if (!savedData) savedData = [];
    savedData.push(data);
    OfflineService.saveItem(
      ID_TOKEN_KEY_REALTIME_SURVEY_DATA,
      JSON.stringify(savedData)
    );
  }
};

export default OfflineService;
